import { deleteAccessTokenCookie } from '@/lib/auth';
import { openAsNewIdentity } from '@/lib/tab-organization';
import { queryClient } from '@/main';

/** Thrown by redirectToSignin so React Query treats the 401 as terminal.
 * The global retry predicate refuses retries for it (#4897). */
export class UnauthorizedError extends Error {
  constructor() {
    super('Unauthorized — session expired, redirecting to signin');
    this.name = 'UnauthorizedError';
  }
}

// One-shot latch: N concurrent 401s must trigger exactly one cleanup +
// navigation, not N cache wipes racing in-flight queries (#4897).
let redirecting = false;

/**
 * Performs authentication cleanup and redirects to signin page.
 * Used when API calls detect invalid/expired tokens.
 *
 * Note: this runs inside queryFn, where a thrown TanStack `redirect()` is NOT
 * honored (that only works in loaders/beforeLoad) — navigation uses a hard
 * location change, which also resets all in-memory state.
 */
export function redirectToSignin(): never {
  if (!redirecting) {
    redirecting = true;
    try {
      deleteAccessTokenCookie();
      queryClient.clear();
    } catch (error) {
      console.error('  ** Error during auth cleanup:', error);
    }
    openAsNewIdentity('/signin');
  }
  throw new UnauthorizedError();
}

/**
 * The validated `redirect` search param on /signin and /signup (#6377).
 *
 * The OAuth consent flow (and any future deep link) sends signed-out users to
 * `/signin?redirect=<in-app path>`; every auth success path resumes there. Only
 * relative in-app paths are accepted: an absolute or protocol-relative value
 * would turn the sign-in page into an open redirect, so anything else is null.
 */
export function safeAuthRedirect(): string | null {
  const raw = new URLSearchParams(window.location.search).get('redirect');
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) {
    return null;
  }
  return raw;
}
